import { useEffect, useMemo } from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { HelpCircle } from "lucide-react";
import { formatArmenianDate } from "@/utils/dateFormatting";

interface CourseFAQProps {
  courseName: string;
  facultyName?: string;
  durationMonths?: number | null;
  weeklyFrequency?: number | null;
  formats?: string[] | null;
  learningTypes?: string[] | null;
  languages?: string[] | null;
  individualPrice?: number | null;
  groupPrice?: number | null;
  startDate?: string | null;
  deadline?: string | null;
  instructors?: string[] | null;
  path: string;
}

const formatLabel = (value: string) => {
  switch (value) {
    case "online":
      return "\u0555\u0576\u056C\u0561\u0575\u0576";
    case "in-person":
    case "inPerson":
    case "offline":
      return "\u0531\u057C\u056F\u0561";
    case "hybrid":
      return "\u0540\u056B\u0562\u0580\u056B\u0564\u0561\u0575\u056B\u0576";
    default:
      return value;
  }
};

const typeLabel = (value: string) => {
  switch (value) {
    case "group":
      return "\u053D\u0574\u0562\u0561\u0575\u056B\u0576";
    case "individual":
      return "\u0531\u0576\u0570\u0561\u057F\u0561\u056F\u0561\u0576";
    default:
      return value;
  }
};

export const CourseFAQ = ({
  courseName,
  facultyName,
  durationMonths,
  weeklyFrequency,
  formats,
  learningTypes,
  languages,
  individualPrice,
  groupPrice,
  startDate,
  deadline,
  instructors,
  path,
}: CourseFAQProps) => {
  const faqs = useMemo(() => {
    const items: { question: string; answer: string }[] = [];

    // \u0548\u0582\u0574 \u0570\u0561\u0574\u0561\u0580 \u0567
    items.push({
      question: `\u0548\u0582\u0574 \u0570\u0561\u0574\u0561\u0580 \u0567 \u00AB${courseName}\u00BB \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568\u0589`,
      answer: `\u00AB${courseName}\u00BB \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u0576\u0561\u056D\u0561\u057F\u0565\u057D\u057E\u0561\u056E \u0567 \u0569\u0565\u055E \u057D\u056F\u057D\u0576\u0561\u056F\u0576\u0565\u0580\u056B, \u0569\u0565\u055E \u0561\u0580\u0564\u0565\u0576 \u0562\u0561\u0566\u0561\u0575\u056B\u0576 \u0563\u056B\u057F\u0565\u056C\u056B\u0584\u0576\u0565\u0580 \u0578\u0582\u0576\u0565\u0581\u0578\u0572\u0576\u0565\u0580\u056B \u0570\u0561\u0574\u0561\u0580\u0589 ${facultyName ? `\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u056F\u0561\u0566\u0574\u0561\u056F\u0565\u0580\u057A\u057E\u0578\u0582\u0574 \u0567 ${facultyName}-\u056B \u056F\u0578\u0572\u0574\u056B\u0581\u0589 ` : ""}\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0576\u0565\u0580\u0568 \u0561\u0576\u0581\u056F\u0561\u0581\u057E\u0578\u0582\u0574 \u0565\u0576 Global IT Academy-\u0578\u0582\u0574\u0589`,
    });

    if (durationMonths) {
      items.push({
        question: `\u0548\u0580\u0584\u0561\u055E\u0576 \u0567 \u057F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0568\u0589`,
        answer: `\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u057F\u0587\u0578\u0582\u0574 \u0567 ${durationMonths} \u0561\u0574\u056B\u057D${weeklyFrequency ? `\u055D \u0577\u0561\u0562\u0561\u0569\u0561\u056F\u0561\u0576 ${weeklyFrequency} \u0585\u0580 \u0570\u0561\u0573\u0561\u056D\u0578\u0582\u0569\u0575\u0561\u0574\u0562` : ""}\u0589 \u0534\u0561\u057D\u056B \u057F\u0587\u0578\u0572\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0568 1.5\u20132 \u056A\u0561\u0574 \u0567\u0589`,
      });
    }

    if (individualPrice || groupPrice) {
      const parts: string[] = [];
      if (groupPrice) parts.push(`\u056D\u0574\u0562\u0561\u0575\u056B\u0576\u055D ${groupPrice.toLocaleString()} \u0564\u0580\u0589/\u0561\u0574\u056B\u057D`);
      if (individualPrice) parts.push(`\u0561\u0576\u0570\u0561\u057F\u0561\u056F\u0561\u0576\u055D ${individualPrice.toLocaleString()} \u0564\u0580\u0589/\u0561\u0574\u056B\u057D`);
      items.push({
        question: "\u0548\u0580\u0584\u0561\u055E\u0576 \u0567 \u0561\u0580\u056A\u0565\u0576\u0561\u0576\u0568\u0589",
        answer: `\u0531\u0580\u056A\u0565\u0584\u0568\u055D ${parts.join(", ")}\u0589 \u054E\u0573\u0561\u0580\u0578\u0582\u0574\u0568 \u056F\u0561\u057F\u0561\u0580\u057E\u0578\u0582\u0574 \u0567 \u0561\u0574\u057D\u0561\u056F\u0561\u0576 \u057D\u056F\u0566\u0562\u0578\u0582\u0576\u0584\u0578\u057E\u0589`,
      });
    }

    if (formats && formats.length > 0) {
      items.push({
        question: "\u053B\u055E\u0576\u0579 \u0571\u0587\u0561\u0579\u0561\u0583\u0578\u057E \u0565\u0576 \u0561\u0576\u0581\u056F\u0561\u0581\u057E\u0578\u0582\u0574 \u0564\u0561\u057D\u0565\u0580\u0568\u0589",
        answer: `\u0534\u0561\u057D\u0565\u0580\u0576 \u0561\u0576\u0581\u056F\u0561\u0581\u057E\u0578\u0582\u0574 \u0565\u0576 ${formats.map(formatLabel).join(", ")} \u0571\u0587\u0561\u0579\u0561\u0583\u0578\u057E${learningTypes && learningTypes.length > 0 ? `\u055D ${learningTypes.map(typeLabel).join(", ")} \u0578\u0582\u057D\u0578\u0582\u0581\u0574\u0561\u0574\u0562` : ""}\u0589`,
      });
    }

    if (startDate || deadline) {
      items.push({
        question: "\u0535\u0580\u0562\u055E \u0567 \u057D\u056F\u057D\u057E\u0578\u0582\u0574 \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568\u0589",
        answer: `${startDate ? `\u0544\u0565\u056F\u0576\u0561\u0580\u056F\u0568\u055D ${formatArmenianDate(startDate)}\u0589 ` : ""}${deadline ? `\u0534\u056B\u0574\u0578\u0582\u0574\u0576\u0565\u0580\u0576 \u0568\u0576\u0564\u0578\u0582\u0576\u057E\u0578\u0582\u0574 \u0565\u0576 \u0574\u056B\u0576\u0579\u0587 ${formatArmenianDate(deadline)}\u0589` : ""}`.trim(),
      });
    }

    if (languages && languages.length > 0) {
      items.push({
        question: "\u053B\u055E\u0576\u0579 \u056C\u0565\u0566\u0578\u0582\u0576\u0565\u0580\u0578\u057E \u0567 \u0564\u0561\u057D\u0561\u057E\u0561\u0576\u0564\u057E\u0578\u0582\u0574\u0589",
        answer: `\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u0561\u0576\u0581\u056F\u0561\u0581\u057E\u0578\u0582\u0574 \u0567 \u0570\u0565\u057F\u0587\u0575\u0561\u056C \u056C\u0565\u0566\u0578\u0582\u0576\u0565\u0580\u0578\u057E\u055D ${languages.join(", ")}\u0589`,
      });
    }

    if (instructors && instructors.length > 0) {
      items.push({
        question: "\u0548\u057E\u0584\u0565\u055E\u0580 \u0565\u0576 \u0564\u0561\u057D\u0561\u057E\u0561\u0576\u0564\u0578\u0582\u0574\u0589",
        answer: `\u0534\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u057E\u0561\u0580\u0578\u0582\u0574 \u0565\u0576 \u0563\u0578\u0580\u056E\u0578\u0572 \u0574\u0561\u057D\u0576\u0561\u0563\u0565\u057F\u0576\u0565\u0580\u055D ${instructors.join(", ")}\u0589`,
      });
    }

    items.push({
      question: "\u0531\u0576\u0570\u0580\u0561\u056A\u0565\u055E\u0577\u057F \u0567 \u0561\u0580\u0564\u0575\u0578\u0584 \u0576\u0561\u056D\u0576\u0561\u056F\u0561\u0576 \u0563\u056B\u057F\u0565\u056C\u056B\u0584\u0568\u0589",
      answer: "\u0548\u0579\u055D \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u057D\u056F\u057D\u057E\u0578\u0582\u0574 \u0567 \u0570\u056B\u0574\u0576\u0561\u056F\u0561\u0576\u0576\u0565\u0580\u056B\u0581\u055D \u0584\u0561\u0575\u056C \u0561\u057C \u0584\u0561\u0575\u056C\u0589 \u0531\u0576\u0570\u0580\u0561\u056A\u0565\u0577\u057F \u0567 \u0574\u056B\u0561\u0575\u0576 \u0570\u0561\u0574\u0561\u056F\u0561\u0580\u0563\u0579\u056B\u0579 \u0585\u0563\u057F\u057E\u0565\u056C\u0578\u0582 \u0570\u0574\u057F\u0578\u0582\u0569\u0575\u0578\u0582\u0576 \u0587 \u057D\u0578\u057E\u0578\u0580\u0565\u056C\u0578\u0582 \u0581\u0561\u0576\u056F\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0589",
    });

    items.push({
      question: "\u054E\u056F\u0561\u0575\u0561\u056F\u0561\u055E\u0576 \u057F\u0580\u057E\u0578\u0582\u0574 \u0567 \u0561\u057E\u0561\u0580\u057F\u0565\u056C\u0578\u0582\u0581 \u0570\u0565\u057F\u0578\u0589",
      answer: "\u0531\u0575\u0578\u055D \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u0568 \u0570\u0561\u057B\u0578\u0572\u0578\u0582\u0569\u0575\u0561\u0574\u0562 \u0561\u057E\u0561\u0580\u057F\u0565\u056C\u0578\u0582 \u0564\u0565\u057A\u0584\u0578\u0582\u0574 \u057F\u0580\u057E\u0578\u0582\u0574 \u0567 Global IT Academy-\u056B \u057E\u056F\u0561\u0575\u0561\u056F\u0561\u0576, \u0578\u0580\u056B \u056B\u057D\u056F\u0578\u0582\u0569\u0575\u0578\u0582\u0576\u0568 \u0570\u0576\u0561\u0580\u0561\u057E\u0578\u0580 \u0567 \u057D\u057F\u0578\u0582\u0563\u0565\u056C \u056F\u0561\u0575\u0584\u056B \u057E\u056F\u0561\u0575\u0561\u0563\u0580\u0565\u0580\u056B \u057D\u057F\u0578\u0582\u0563\u0574\u0561\u0576 \u0567\u057B\u0578\u0582\u0574\u0589",
    });

    items.push({
      question: "\u053B\u0576\u0579\u057A\u0565\u055E\u057D \u0563\u0580\u0561\u0576\u0581\u057E\u0565\u056C \u0564\u0561\u057D\u0568\u0576\u0569\u0561\u0581\u056B\u0576\u0589",
      answer: "\u0533\u0580\u0561\u0576\u0581\u057E\u0565\u056C\u0578\u0582 \u0570\u0561\u0574\u0561\u0580 \u056C\u0580\u0561\u0581\u0580\u0565\u0584 \u0567\u057B\u0578\u0582\u0574 \u0561\u0580\u0564\u0565\u0576 \u0561\u057C\u056F\u0561 \u0570\u0561\u0575\u057F\u0561\u0564\u056B\u0574\u0568\u055D \u0576\u0577\u0565\u056C\u0578\u057E \u0561\u0576\u0578\u0582\u0576\u0568, \u0570\u0565\u057C\u0561\u056D\u0578\u057D\u0561\u0570\u0561\u0574\u0561\u0580\u0568 \u0587 \u0567\u056C. \u0583\u0578\u057D\u057F\u0568\u0589 \u0544\u0565\u0576\u0584 \u056F\u056F\u0561\u057A\u057E\u0565\u0576\u0584 \u0571\u0565\u0566 \u0570\u0565\u057F \u0574\u0561\u0576\u0580\u0561\u0574\u0561\u057D\u0576\u0565\u0580\u0568 \u0573\u0577\u057F\u0565\u056C\u0578\u0582 \u0570\u0561\u0574\u0561\u0580\u0589",
    });

    return items;
  }, [courseName, facultyName, durationMonths, weeklyFrequency, formats, learningTypes, languages, individualPrice, groupPrice, startDate, deadline, instructors]);

  useEffect(() => {
    const id = "course-faq-jsonld";
    document.getElementById(id)?.remove();
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = id;
    script.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `https://globalitacademy.am${path}#faq`,
      mainEntity: faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
    document.head.appendChild(script);
    return () => {
      document.getElementById(id)?.remove();
    };
  }, [faqs, path]);

  return (
    <section id="faq" className="scroll-mt-28">
      <span className="section-kicker">FAQ</span>
      <h2 className="mt-2 font-display text-2xl font-bold text-foreground sm:text-3xl">
        <HelpCircle className="mr-2 inline h-6 w-6 text-primary" aria-hidden="true" />
        {"\u0540\u0561\u0573\u0561\u056D \u057F\u0580\u057E\u0578\u0572 \u0570\u0561\u0580\u0581\u0565\u0580"}
      </h2>
      <Accordion type="single" collapsible className="mt-5 space-y-3">
        {faqs.map((faq, index) => (
          <AccordionItem
            key={index}
            value={`faq-${index}`}
            className="overflow-hidden rounded-lg border border-border/70 bg-card/70 px-4 transition-colors data-[state=open]:border-primary/40 sm:px-5"
          >
            <AccordionTrigger className="min-h-14 gap-4 py-4 text-left text-base font-semibold hover:no-underline">
              {faq.question}
            </AccordionTrigger>
            <AccordionContent className="border-t border-border/60 pb-5 pt-4 text-muted-foreground leading-7">
              {faq.answer}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};
